import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../config/paths";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { MagnifyingGlass, Plus, Rows, SquaresFour } from "@phosphor-icons/react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import FilterPill from "./FilterPill";
import SupplierStatusBadge from "./SupplierStatusBadge";
import SupplierCard from "./SupplierCard";
import AddSupplierCard from "./AddSupplierCard";
import SupplierActionsMenu from "./SupplierActionsMenu";
import SupplierFormModal from "./SupplierFormModal";
import ChangeStatusModal from "./ChangeStatusModal";
import LocalSpinner from "../../../components/LocalSpinner";
import {
  constructQueryParamsString,
  QUESTIONNAIRE_STATUSES,
  showErrorToast,
  showSuccessToast,
} from "../../../utils";
import {
  supplierAdministrationKeys,
  useGetPaginatedSuppliersWithTiers,
  useManageSupplierCompany,
  useManageSupplierStatus,
} from "../../../hooks/query/supplier-administration";
import { useGetAllSupplierCategories } from "../../../hooks/query";
import { useGetSupplierEngagementReports } from "../../../hooks/query/reports";
import { useGetSuppliersYearlyData } from "../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { SUPPLIER_STATUS_OPTIONS, REPORTING_YEARS } from "../suppliersMock";

// Suppliers tab — list/card view of suppliers with search + category/status
// filters and add/edit/status actions backed by the real API.
function SuppliersTab() {
  const navigate = useNavigate();
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const goToSupplier = (supplier) =>
    navigate(
      routes.supplyChainSupplierDetail.replace(":supplierId", supplier.id),
      { state: { supplier } }
    );

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [viewType, setViewType] = useState("list");

  const formModal = useDisclosure();
  const [editTarget, setEditTarget] = useState(null);
  const statusModal = useDisclosure();
  const [statusTarget, setStatusTarget] = useState(null);

  // Primary source — same endpoint as the supplier portal and Insights dashboards.
  // Response: { [supplierName]: { [username]: [{ year, status, type, questionnaireId }] } }
  const { data: engagementData, isFetching: engagementFetching } = useGetSupplierEngagementReports(null, null, {
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  // Yearly emission data — used only to extract the UUID supplierId per supplier
  const yearlyQueryString = useMemo(
    () => constructQueryParamsString({ years: REPORTING_YEARS, showExcluded: true, showSupplierWithoutData: true }),
    []
  );
  const { data: yearlyData } = useGetSuppliersYearlyData(yearlyQueryString, { cacheTime: 60 });
  const uuidByName = useMemo(() => {
    const map = {};
    (yearlyData ?? []).forEach((row) => { if (row.supplierName) map[row.supplierName] = row.supplierId; });
    return map;
  }, [yearlyData]);

  // Tiers data — enrichment only: country, status, category, id (for navigation)
  const tiersQueryString = useMemo(
    () => constructQueryParamsString({ page: 0, size: 500, sort: ["supplier.name,ASC"] }),
    []
  );
  const { data: suppliersPage } = useGetPaginatedSuppliersWithTiers(tiersQueryString, { cacheTime: 60 });
  // Key tiers by supplier name for easy lookup
  const tiersByName = useMemo(() => {
    const map = {};
    (suppliersPage?.content ?? []).forEach((el) => {
      map[el.name] = el;
    });
    return map;
  }, [suppliersPage]);

  // Build the canonical supplier list: one row per supplier name from the engagement data
  const suppliers = useMemo(() => {
    if (!engagementData) return [];
    return Object.keys(engagementData)
      .sort()
      .map((name) => {
        const tier = tiersByName[name];
        const supplierId = uuidByName[name] ?? null;
        // Per-year questionnaire status: pick the best status across all users for that year
        const yearStatus = {};
        REPORTING_YEARS.forEach((y) => {
          let best = "notStarted";
          Object.values(engagementData[name]).forEach((questionnaires) => {
            const q = questionnaires.find((x) => x.year === y);
            if (!q) return;
            if (q.status === QUESTIONNAIRE_STATUSES.FINALIZED) best = "finalised";
            else if (q.status === QUESTIONNAIRE_STATUSES.IN_PROGRESS && best !== "finalised") best = "inProgress";
            else if (q.status === QUESTIONNAIRE_STATUSES.NEW && best === "notStarted") best = "started";
          });
          yearStatus[y] = best;
        });
        return {
          id: tier?.id ?? name,
          supplierId,
          name,
          category: tier?.category?.name ?? tier?.category ?? "",
          categoryId: tier?.category?.id ?? null,
          tier: tier?.tier ?? null,
          country: tier?.country ?? "",
          status: tier?.status ?? null,
          userCount: Object.keys(engagementData[name]).length,
          yearStatus,
          engagementData: engagementData[name],
        };
      });
  }, [engagementData, tiersByName, uuidByName]);

  const isFetching = engagementFetching;

  // Real category options from the API
  const { data: categoriesData } = useGetAllSupplierCategories({});
  const categoryOptions = useMemo(
    () => (categoriesData ?? []).map((c) => ({ value: c.name, label: c.name, id: c.id })),
    [categoriesData]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const cats = new Set(categoryFilter.map((c) => c.value));
    const statuses = new Set(statusFilter.map((s) => s.value));
    return suppliers.filter((s) => {
      if (term && !`${s.name} ${s.country}`.toLowerCase().includes(term)) return false;
      if (cats.size && !cats.has(s.category)) return false;
      if (statuses.size && !statuses.has(s.status)) return false;
      return true;
    });
  }, [suppliers, search, categoryFilter, statusFilter]);

  const invalidate = () => {
    queryClient.invalidateQueries(supplierAdministrationKeys.paginatedSuppliersWithTiers());
    queryClient.invalidateQueries(["supplierEngagementReports"]);
  };

  const { mutate: saveSupplier } = useManageSupplierCompany({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => { showSuccessToast(toast, "Supplier saved"); formModal.onClose(); invalidate(); },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const { mutate: changeStatus } = useManageSupplierStatus({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => { showSuccessToast(toast, "Status updated"); statusModal.onClose(); invalidate(); },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const openAdd = () => { setEditTarget(null); formModal.onOpen(); };
  const openEdit = (supplier) => { setEditTarget(supplier); formModal.onOpen(); };
  const openChangeStatus = (supplier) => { setStatusTarget(supplier); statusModal.onOpen(); };

  const handleSave = (formData) => {
    const categoryId = categoryOptions.find((c) => c.value === formData.category)?.id ?? formData.categoryId ?? null;
    saveSupplier({
      id: typeof formData.id === "number" ? formData.id : null,
      name: formData.name,
      categoryId,
      tier: formData.tier ? Number(formData.tier) : null,
      country: formData.country ?? null,
    });
  };

  const handleSaveStatus = (status) => {
    if (!statusTarget) return;
    changeStatus({ id: statusTarget.id, status });
  };

  if (isFetching && suppliers.length === 0) return <Box mt={6}><LocalSpinner /></Box>;

  const hasSearch = search.trim().length > 0;

  return (
    <Box mt={6}>
      {/* Controls row */}
      <Flex justify="space-between" align="center" gap={3} mb={4} flexWrap="wrap">
        <HStack spacing={2} flexWrap="nowrap" flexShrink={0}>
          <InputGroup size="sm" maxW="240px">
            <InputLeftElement pointerEvents="none">
              <MagnifyingGlass size={14} color="#A0AEC0" />
            </InputLeftElement>
            <Input
              borderRadius="full"
              placeholder="Search suppliers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              borderColor={hasSearch ? "lime" : "gray.200"}
              bg={hasSearch ? "lightLime" : "white"}
              _hover={{ borderColor: "lime" }}
              _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
            />
          </InputGroup>
          <FilterPill label="Category" options={categoryOptions} selected={categoryFilter} onChange={setCategoryFilter} />
          <FilterPill label="Status" options={SUPPLIER_STATUS_OPTIONS} selected={statusFilter} onChange={setStatusFilter} />
        </HStack>

        <HStack spacing={3}>
          {viewType === "list" && (
            <Button
              bg="limeShade.600"
              color="white"
              borderRadius="full"
              _hover={{ bg: "limeShade.700" }}
              size="sm"
              leftIcon={<Plus size={14} weight="bold" />}
              onClick={openAdd}
            >
              Add supplier
            </Button>
          )}
          <Flex bg="gray.100" borderRadius="full" p="3px">
            <Button
              size="sm"
              borderRadius="full"
              bg={viewType === "list" ? "darkTeal" : "transparent"}
              color={viewType === "list" ? "white" : "gray.500"}
              _hover={{ bg: viewType === "list" ? "darkTeal" : "gray.300" }}
              rightIcon={<Rows size={16} />}
              fontWeight="600"
              fontSize="13px"
              px={4}
              onClick={() => setViewType("list")}
            >
              List
            </Button>
            <Button
              size="sm"
              borderRadius="full"
              bg={viewType === "grid" ? "darkTeal" : "transparent"}
              color={viewType === "grid" ? "white" : "gray.500"}
              _hover={{ bg: viewType === "grid" ? "darkTeal" : "gray.300" }}
              rightIcon={<SquaresFour size={16} />}
              fontWeight="600"
              fontSize="13px"
              px={4}
              onClick={() => setViewType("grid")}
            >
              Grid
            </Button>
          </Flex>
        </HStack>
      </Flex>

      <Text fontSize="13px" color="gray.500" mb={3}>
        {filtered.length} of {suppliers.length} suppliers
      </Text>

      {viewType === "grid" ? (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
          <AddSupplierCard onAdd={openAdd} />
          {filtered.map((s) => (
            <SupplierCard
              key={s.id}
              supplier={s}
              onOpen={() => goToSupplier(s)}
              onEdit={() => openEdit(s)}
              onChangeStatus={() => openChangeStatus(s)}
            />
          ))}
        </SimpleGrid>
      ) : filtered.length === 0 ? (
        <Box border="1px dashed" borderColor="gray.200" borderRadius="md" py={12} textAlign="center">
          <Text color="gray.400" fontSize="14px">No suppliers match your filters.</Text>
        </Box>
      ) : (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
          <Table size="sm" variant="unstyled">
            <Thead>
              <Tr bg="gray.50">
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Supplier</Th>
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Category</Th>
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Country</Th>
                {REPORTING_YEARS.map((year) => (
                  <Th key={year} py={3} px={3} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="center">{year}</Th>
                ))}
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Status</Th>
                <Th py={3} px={4} width="56px" />
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((s, idx) => (
                <Tr key={s.id} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100" _hover={{ bg: "gray.50" }}>
                  <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="600">
                    <Text
                      as="button"
                      textAlign="left"
                      _hover={{ color: "limeShade.700", textDecoration: "underline" }}
                      onClick={() => goToSupplier(s)}
                    >
                      {s.name}
                    </Text>
                  </Td>
                  <Td py={3} px={4} fontSize="14px" color="gray.700">{s.category || "—"}</Td>
                  <Td py={3} px={4} fontSize="14px" color="gray.700">{s.country || "—"}</Td>
                  {REPORTING_YEARS.map((year) => {
                    const status = s.yearStatus[year] ?? "notStarted";
                    const dotColor =
                      status === "finalised" ? "status.green"
                      : status === "inProgress" ? "status.orange"
                      : status === "started" ? "blue.300"
                      : "gray.200";
                    const label =
                      status === "finalised" ? "Finalised"
                      : status === "inProgress" ? "In progress"
                      : status === "started" ? "Started"
                      : "Not started";
                    return (
                      <Td key={year} py={3} px={3} textAlign="center">
                        <Tooltip label={`${year}: ${label}`} hasArrow placement="top">
                          <Box w="10px" h="10px" borderRadius="full" bg={dotColor} mx="auto" cursor="default" />
                        </Tooltip>
                      </Td>
                    );
                  })}
                  <Td py={3} px={4}><SupplierStatusBadge status={s.status} /></Td>
                  <Td py={3} px={4} textAlign="right">
                    <SupplierActionsMenu onEdit={() => openEdit(s)} onChangeStatus={() => openChangeStatus(s)} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <SupplierFormModal
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        onSave={handleSave}
        supplier={editTarget}
      />
      <ChangeStatusModal
        isOpen={statusModal.isOpen}
        onClose={statusModal.onClose}
        onSave={handleSaveStatus}
        supplier={statusTarget}
      />
    </Box>
  );
}

export default SuppliersTab;
